import { Assert } from "../../Assert";
import { DataSource } from "../../Data/DataSource";
import { DataSourcePropertyValueChange } from "../../Data/DataSourcePropertyValueChange";
import { PartialNullable } from "../../Extensions";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyObject, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { BuiltInTypes } from "../../Server/DotNet/System/BuiltInTypes";
import { JsxFragment } from "../JsxFragment";
import { ComboBox } from "./ComboBox";
import { ControlRef } from "./ControlRef";
import { Label } from "./Label";
import { IPropertyBoundControlMixin, PropertyBoundControlMixin } from "./Mixins/PropertyBoundControlMixin";
import { IToolbarItem, ToolbarItem, ToolbarItemPropertyValues } from "./ToolbarItem";
import { NAMESPACE } from "./_Namespace";

import "./ToolbarComboBox.scss";

export type ToolbarComboBoxPropertyValues =
    ToolbarItemPropertyValues
    & {
        allowEmptySelection: boolean;
        dataSourceProperty: string;
        dataSourcePropertyType: BuiltInTypes;
        listDataSource: DataSource;
        listDataSourceValueProperty: string;
        listDataSourceDescriptionProperty: string;
        placeholder: string;
        value: any;
    };

export type ToolbarComboBoxProperties = {
    [name in keyof ToolbarComboBoxPropertyValues]: Property<ToolbarComboBoxPropertyValues[name]>
};

export interface IToolbarComboBox extends IToolbarItem, ToolbarComboBoxProperties, IPropertyBoundControlMixin
{
}

export class ToolbarComboBox extends ToolbarItem implements IToolbarComboBox
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ToolbarComboBox";
    public static override className = "ToolbarComboBox";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ToolbarComboBoxPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                listDataSourceDescriptionProperty: "description",
                listDataSourceValueProperty: "value",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _comboBoxChanging?: boolean;
    protected _comboBoxControlRef!: ControlRef<ComboBox>;
    protected _labelControlRef!: ControlRef<Label>;

    // #endregion

    // #region Properties

    public allowEmptySelection!: PropertyBoolean;
    public dataSourceProperty!: PropertyString;
    public dataSourcePropertyType!: Property<BuiltInTypes>;
    public listDataSource!: Property<DataSource>;
    public listDataSourceValueProperty!: PropertyString;
    public listDataSourceDescriptionProperty!: PropertyString;
    public placeholder!: PropertyString;
    public value!: PropertyObject;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ToolbarComboBoxPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-toolbar-combo-box item");

        // Trigger Property Changes
        this.allowEmptySelection.triggerChange();
        this.listDataSourceValueProperty.triggerChange();
        this.listDataSourceDescriptionProperty.triggerChange();
        this.listDataSource.triggerChange();
        this.placeholder.triggerChange();

        PropertyBoundControlMixin.construct(this);
    }

    // #endregion

    // #region Protected Methods

    protected _allowEmptySelectionChanged(change: PropertyChange<boolean>)
    {
        Assert.isNotNullOrUndefined(this._comboBoxControlRef.current, "this._comboBoxControlRef.current");
        this._comboBoxControlRef.current.allowEmptySelection.set(change.currentValue);
    }
    protected _listDataSourceChanged(change: PropertyChange<DataSource>)
    {
        Assert.isNotNullOrUndefined(this._comboBoxControlRef.current, "this._comboBoxControlRef.current");
        this._comboBoxControlRef.current.listDataSource.set(change.currentValue);
    }
    protected _listDataSourceDescriptionPropertyChanged(change: PropertyChange<string>)
    {
        Assert.isNotNullOrUndefined(this._comboBoxControlRef.current, "this._comboBoxControlRef.current");
        this._comboBoxControlRef.current.listDataSourceDescriptionProperty.set(change.currentValue);
    }
    protected _listDataSourceValuePropertyChanged(change: PropertyChange<string>)
    {
        Assert.isNotNullOrUndefined(this._comboBoxControlRef.current, "this._comboBoxControlRef.current");
        this._comboBoxControlRef.current.listDataSourceValueProperty.set(change.currentValue);
    }
    protected _placeholderChanged(change: PropertyChange<string>)
    {
        Assert.isNotNullOrUndefined(this._comboBoxControlRef.current, "this._comboBoxControlRef.current");
        this._comboBoxControlRef.current.placeholder.set(change.currentValue);
    }


    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ToolbarComboBoxPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._comboBoxControlRef = new ControlRef();
        this._labelControlRef = new ControlRef();

        this.allowEmptySelection = new PropertyBoolean(this, propertyValues.allowEmptySelection, this._allowEmptySelectionChanged);
        this.dataSourceProperty = new PropertyString({
            context: this,
            initialValue: propertyValues.dataSourceProperty,
            changeHandler: this._dataSourcePropertyChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Data Source Property" }),
            ],
        });
        this.dataSourcePropertyType = new Property<BuiltInTypes>({
            context: this,
            dotNetType: "System.String, System.Private.CoreLib",
            initialValue: propertyValues.dataSourcePropertyType,
            changeHandler: this._dataSourcePropertyTypeChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Data Source Property Type" }),
            ],
        });
        this.listDataSource = new Property<DataSource>({
            context: this,
            dotNetType: "System.Object, System.Private.CoreLib",
            initialValue: propertyValues.listDataSource,
            changeHandler: this._listDataSourceChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "List Data Source" }),
            ],
        });
        this.listDataSourceValueProperty = new PropertyString({
            context: this,
            initialValue: propertyValues.listDataSourceValueProperty,
            changeHandler: this._listDataSourceValuePropertyChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "List Data Source Value Property" }),
            ],
        });
        this.listDataSourceDescriptionProperty = new PropertyString({
            context: this,
            initialValue: propertyValues.listDataSourceDescriptionProperty,
            changeHandler: this._listDataSourceDescriptionPropertyChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "List Data Source Description Property" }),
            ],
        });
        this.placeholder = new PropertyString({
            context: this,
            initialValue: propertyValues.placeholder,
            changeHandler: this._placeholderChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Placeholder" }),
            ],
        });
        this.value = new PropertyObject({
            context: this,
            initialValue: propertyValues.value,
            changeHandler: this._valueChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Value" }),
            ],
        });

        this.listDataSource.overrideSet((value, next) =>
        {
            if (!(value instanceof DataSource))
                value = new DataSource({ data: value });

            next(value);
        });
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        Assert.isNotNullOrUndefined(this._comboBoxControlRef.current, "this._comboBoxControlRef.current");

        this._comboBoxControlRef.current.on("valueChanged", { self: this }, this.onComboBoxValueChanged);
        this.on("valueChanged", { self: this }, this.onValueChanged);
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <div>
                <span ref={this._containerElementRef} class="rad-container">
                    <Label ref={this._labelControlRef} associatedControlRef={this._comboBoxControlRef} colon={false}></Label>
                    <ComboBox ref={this._comboBoxControlRef} inline={true}></ComboBox>
                    {children}
                </span>
            </div>
        );
    }
    protected override _enabledChanged(change: PropertyChange<boolean>)
    {
        Assert.isNotNullOrUndefined(this._comboBoxControlRef.current, "this._comboBoxControlRef.current");
        this._comboBoxControlRef.current.enabled.set(change.currentValue);
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);

        Assert.isNotNullOrUndefined(this._labelControlRef.current, "this._labelControlRef.current");
        this._labelControlRef.current.text.set(change.currentValue);
    }

    // #endregion

    // #region IPropertyBoundControlMixin

    public _dataSourcePropertyChanged(change: PropertyChange<string>): void
    {
        PropertyBoundControlMixin._dataSourcePropertyChanged(this, change);
    }
    public _dataSourcePropertyTypeChanged(change: PropertyChange<string>): void
    {
        PropertyBoundControlMixin._dataSourcePropertyTypeChanged(this, change);
    }
    public _getDataSourceValue(): any
    {
        return PropertyBoundControlMixin._getDataSourceValue(this);
    }
    public override _refreshFromDataSource(): void
    {
        super._refreshFromDataSource();
        PropertyBoundControlMixin._refreshFromDataSource(this);
    }
    public _setDataSourceValue(value: any, inhibitEvent?: boolean): void
    {
        PropertyBoundControlMixin._setDataSourceValue(this, value, inhibitEvent);
    }
    public _valueChanged(change: PropertyChange<any>): void
    {
        PropertyBoundControlMixin._valueChanged(this, change);
    }

    public override onDataSourcePropertyValueChanged(evt: IJNativeEvent, change: DataSourcePropertyValueChange<any>): void
    {
        super.onDataSourcePropertyValueChanged(evt, change);
        PropertyBoundControlMixin.onDataSourcePropertyValueChanged(this, evt, change);
    }

    // #endregion

    // #region Event Handlers

    protected onComboBoxValueChanged(evt: IJNativeEvent, change: PropertyChange<any>)
    {
        const
            self = evt.data.self as this;

        if (self._comboBoxChanging)
            return;

        self._comboBoxChanging = true;
        self.value.set(change.currentValue);
        self._comboBoxChanging = false;
    }
    protected onValueChanged(evt: IJNativeEvent, change: PropertyChange<any>)
    {
        const
            self = evt.data.self as this;

        if (!self._comboBoxChanging)
            self._comboBoxControlRef.current?.value.set(change.currentValue);
    }

    // #endregion

    // #endregion
}

